import { useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError } from "@/lib/studioApi";

// Enregistreur vocal : l'artisan parle, Whisper transcrit, Claude structure.
export const VoiceRecorder = ({ dossierId, onResult }) => {
  const [state, setState] = useState("idle"); // idle | recording | sending
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 1200) {
          setState("idle");
          toast.error("Enregistrement trop court.");
          return;
        }
        setState("sending");
        const ext = mimeType.includes("webm") ? "webm" : "mp4";
        const fd = new FormData();
        fd.append("file", new File([blob], `dictee.${ext}`, { type: mimeType }));
        try {
          const { data } = await studio.post(`/dossiers/${dossierId}/voice`, fd, { timeout: 240000 });
          toast.success("Dictée analysée par l'assistant.");
          onResult?.(data);
        } catch (e) {
          toast.error(apiError(e, "Analyse de la dictée impossible. Réessayez."));
        } finally {
          setState("idle");
          setSeconds(0);
        }
      };
      mediaRef.current = recorder;
      recorder.start();
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Micro inaccessible. Autorisez le microphone dans votre navigateur.");
    }
  };

  const stop = () => {
    stopTimer();
    mediaRef.current?.stop();
  };

  return (
    <div data-testid="voice-recorder" className="rounded-2xl border border-[#3A5CC8]/20 bg-gradient-to-br from-blue-50 to-white p-5">
      <p className="text-sm font-semibold text-[#0F2645] inline-flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#3A5CC8]" /> Parler à Devisto
      </p>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">
        Décrivez le chantier à voix haute : l'assistant transcrit, structure les informations et signale ce qui manque.
      </p>
      <div className="mt-4 flex items-center gap-3">
        {state !== "recording" ? (
          <button
            type="button"
            data-testid="voice-start-btn"
            disabled={state === "sending"}
            onClick={start}
            className="inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-5 py-3 text-sm font-semibold text-white hover:bg-[#2F4CA8] disabled:opacity-40 active:scale-95 transition-all duration-200"
          >
            {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
            {state === "sending" ? "Analyse en cours…" : "Dicter mes constats"}
          </button>
        ) : (
          <button
            type="button"
            data-testid="voice-stop-btn"
            onClick={stop}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-3 text-sm font-semibold text-white hover:bg-red-700 active:scale-95 transition-all duration-200"
          >
            <Square className="w-4 h-4" /> Terminer ({seconds}s)
          </button>
        )}
        {state === "recording" && (
          <span data-testid="voice-recording-indicator" className="inline-flex items-center gap-2 text-xs font-semibold text-red-600">
            <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" /> Enregistrement…
          </span>
        )}
      </div>
    </div>
  );
};
